import { combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';
import gameReducer, { createInitialGameState, DEFAULT_GAME_SETTINGS } from '../features/game/gameSlice';
import { GameSettings, GameState, Player, RoundVariant } from '../types/game.types';
import { getCardsDealtForRound, getRoundPlan } from '../utils/gameRules';
import { normalizePlayerAppearance } from '../features/mascots/appearance';
import { synchronizeStoredPlayerIdentities } from '../features/mascots/playerIdentity';
import { PLAYER_COLORS } from '../utils/constants';

export interface RootState {
  game: GameState;
}

type StoredGameState = Partial<GameState>;

const GAME_STATE_KEY = 'skullking_state_v3';
const PREVIOUS_GAME_STATE_KEY = 'skullking_state_v2';
const SETTINGS_KEY = 'skullking_settings_v2';
const LEGACY_GAME_STATE_KEY = 'skullking_state';

function safeRead<T>(key: string): T | null {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch (error) {
    console.error('Impossible de relire les données locales:', error);
    return null;
  }
}

function isRoundVariant(value: unknown): value is RoundVariant {
  return typeof value === 'string' && ['classic', 'evenKeeled', 'skip', 'fiveOfFive', 'tenOfTen', 'whirlpool', 'single'].includes(value);
}

function getStoredRoundPlan(source: StoredGameState, settings: GameSettings, rulesMode: GameState['rulesMode'], playerCount: number): number[] {
  if (Array.isArray(source.roundPlan) && source.roundPlan.length > 0 && source.roundPlan.every(value => Number.isInteger(value) && value > 0)) {
    return source.roundPlan;
  }

  if (rulesMode === 'simplified') return Array.from({ length: 10 }, (_, index) => index + 1);
  return getRoundPlan(settings.roundVariant).map(cards => getCardsDealtForRound(cards, playerCount));
}

function normaliseGameState(rawState: StoredGameState | null, savedSettings: Partial<GameSettings> | null): GameState {
  const base = createInitialGameState();
  const source = rawState || {};
  const candidateVariant = source.settings?.roundVariant || savedSettings?.roundVariant;
  const settings: GameSettings = {
    ...DEFAULT_GAME_SETTINGS,
    ...(source.settings || {}),
    ...(savedSettings || {}),
    roundVariant: isRoundVariant(candidateVariant) ? candidateVariant : DEFAULT_GAME_SETTINGS.roundVariant
  };
  const players = Array.isArray(source.players)
    ? source.players.map((player: Player, index) => ({
        ...player,
        appearance: normalizePlayerAppearance(player.appearance, player.id, index, PLAYER_COLORS.length),
        predictions: player.predictions || [],
        results: player.results || [],
        roundDetails: player.roundDetails || []
      }))
    : [];
  const rulesMode = source.rulesMode === 'complete' ? 'complete' : source.rulesMode === 'simplified' ? 'simplified' : (settings.completeRules ? 'complete' : 'simplified');
  const roundPlan = getStoredRoundPlan(source, settings, rulesMode, players.length);
  const currentRound = Number.isInteger(source.currentRound) && Number(source.currentRound) > 0 ? Number(source.currentRound) : 1;
  const legacyFinished = currentRound > roundPlan.length;
  const legacyId = players.length ? `legacy-${source.createdAt || 'saved'}-${players.map(player => player.id).join('-')}` : null;
  const activeRoundVariant = isRoundVariant(source.roundVariant)
    ? source.roundVariant
    : (rulesMode === 'complete' ? settings.roundVariant : null);
  const sourceRascalScoring = typeof source.rascalScoring === 'boolean'
    ? source.rascalScoring
    : Boolean(rulesMode === 'complete' && settings.rascalScoring);

  return {
    ...base,
    players,
    currentRound,
    settings,
    rulesMode,
    roundPlan,
    roundVariant: activeRoundVariant,
    rascalScoring: rulesMode === 'complete' && sourceRascalScoring,
    gameId: typeof source.gameId === 'string' ? source.gameId : legacyId,
    createdAt: typeof source.createdAt === 'number' ? source.createdAt : null,
    completedAt: typeof source.completedAt === 'number' ? source.completedAt : (legacyFinished ? Date.now() : null)
  };
}

function loadState(): RootState | undefined {
  const savedSettings = safeRead<Partial<GameSettings>>(SETTINGS_KEY);
  const current = safeRead<RootState>(GAME_STATE_KEY);
  const previous = current ? null : safeRead<{ game?: StoredGameState }>(PREVIOUS_GAME_STATE_KEY);
  const legacy = current || previous ? null : safeRead<{ game?: StoredGameState }>(LEGACY_GAME_STATE_KEY);
  const rawGame = current?.game || previous?.game || legacy?.game || null;

  if (!rawGame && !savedSettings) return undefined;
  return { game: normaliseGameState(rawGame, savedSettings) };
}

const persistMiddleware: Middleware<{}, RootState> = store => next => action => {
  const result = next(action);
  const state = store.getState();

  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.game.settings));
    if (state.game.settings.autoSave) {
      localStorage.setItem(GAME_STATE_KEY, JSON.stringify(state));
      localStorage.removeItem(PREVIOUS_GAME_STATE_KEY);
      localStorage.removeItem(LEGACY_GAME_STATE_KEY);
    } else {
      localStorage.removeItem(GAME_STATE_KEY);
      localStorage.removeItem(PREVIOUS_GAME_STATE_KEY);
      localStorage.removeItem(LEGACY_GAME_STATE_KEY);
    }
  } catch (error) {
    console.error('Impossible de sauvegarder les données locales:', error);
  }

  return result;
};

const rootReducer = combineReducers({
  game: gameReducer
});

synchronizeStoredPlayerIdentities();

export const store = configureStore({
  reducer: rootReducer,
  preloadedState: loadState(),
  middleware: getDefaultMiddleware => getDefaultMiddleware().concat(persistMiddleware)
});

export type AppDispatch = typeof store.dispatch;
